import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { SPOTIFY_CODE, SPOTIFY_REFRESH_TOKEN, SPOTIFY_TOKEN } from '../../constants/storage';
import { useLoginContext } from '../../context';
import { usePlaylistContext } from '../../context/PlaylistContext';
import { usePackageContext } from '../../context/PackageContext';
import { getItemFromStorage } from '../../utils';
import {
  FREE_PLAN_COMPLETION_PATH,
  resolveUserBillingIds,
  shouldDowngradeToFreePlan,
} from '../../utils/subscriptionStatus';

import AudioPlayer from '../AudioPlayer/AudioPlayer';

type AuthenticatedRouteProps = {
  children: JSX.Element;
  redirectTo: string;
};

const normalizeEmail = (value: unknown): string =>
  value?.toString()?.trim()?.toLowerCase() || '';

const isActivePaymentStatus = (status: unknown): boolean =>
  status === 'active' || status === 'trialing';

const isFreePlanUser = (local: any): boolean =>
  local?.free === true || local?.planId === 'free' || local?.planId === 'Free';

/**
 * True when packages payload includes an active/trialing subscription for this email.
 * Does not mutate the packages array (critical — PackageContext caches the same reference).
 */
const hasMatchingActivePackage = (packages: any[], email: string): boolean => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail || !Array.isArray(packages) || packages.length === 0) {
    return false;
  }

  return packages.some(
    pkg =>
      normalizeEmail(pkg?.email) === normalizedEmail && isActivePaymentStatus(pkg?.payment_status)
  );
};

const AuthenticatedRoute = ({ children, redirectTo }: AuthenticatedRouteProps) => {
  const { dispatch } = useLoginContext();
  const { fetchPackageData, clearCache } = usePackageContext();

  const token = getItemFromStorage(SPOTIFY_TOKEN);
  const refreshToken = getItemFromStorage(SPOTIFY_REFRESH_TOKEN);
  const code = getItemFromStorage(SPOTIFY_CODE);
  const userInfo = localStorage.getItem('userInfo');
  const spotifyUser = localStorage.getItem('spotifyUser');
  const navigate = useNavigate();
  const location = useLocation();
  const local1 = (() => {
    try {
      return JSON.parse(localStorage.getItem('userInfo') || 'null');
    } catch {
      return null;
    }
  })();
  const { state: playlist } = usePlaylistContext();
  const [isPlaying, setPlaying] = useState(false);
  const [showCurrent, setCurrent] = useState(false);
  useEffect(() => {
    setPlaying(true);
  }, [playlist.previewSong.url]);

  useEffect(() => {
    let cancelled = false;

    const local = (() => {
      try {
        return JSON.parse(localStorage.getItem('userInfo') || 'null');
      } catch {
        return null;
      }
    })();

    if (location) {
      if (location?.pathname === '/') {
        setCurrent(true);
      } else {
        setCurrent(false);
      }
    } else {
      setCurrent(false);
    }

    if (!local) {
      if (location.pathname !== '/') {
        navigate('/');
      }
      return () => {
        cancelled = true;
      };
    }

    // Free-plan users (including Option A auto-free) have no Stripe package.
    // Allow them through without requiring active/trialing subscription.
    const grantAccess = () => {
      if (cancelled) return;
      if (!userInfo) {
        navigate(redirectTo);
        return;
      }
      // If Spotify is connected, dispatch tokens
      if (spotifyUser && token && refreshToken) {
        // Only dispatch code if it exists (shared profiles may not have it)
        if (code) {
          dispatch({
            type: 'ADD_APP_CODE',
            payload: code,
          });
        }

        dispatch({
          type: 'ADD_APP_TOKEN',
          payload: {
            token: token,
            refreshToken: refreshToken,
          },
        });
      }
      dispatch({
        type: 'SET_LOGIN',
        payload: true,
      });
    };

    const bounceToShift = () => {
      if (cancelled) return;
      const accessToken = local?.token;
      if (!accessToken) {
        navigate('/');
        return;
      }
      // Avoid hammering full reloads if we are already mid-bounce.
      window.location.replace(`/shift#access_token=${accessToken}`);
    };

    const completeFreeDowngrade = () => {
      if (cancelled) return;
      clearCache();
      window.location.replace(FREE_PLAN_COMPLETION_PATH);
    };

    if (isFreePlanUser(local)) {
      grantAccess();
      return () => {
        cancelled = true;
      };
    }

    fetchPackageData(local)
      .then(data => {
        if (cancelled) return;

        // Transient/null response (in-flight race, network blip): never bounce to /shift.
        // Bouncing on null was causing shift ↔ dashboard loops.
        if (data == null) {
          // If we already know a paid plan id from session, allow access rather than looping.
          if (local?.planId && local.planId !== 'free' && local.planId !== 'Free') {
            grantAccess();
          }
          return;
        }

        const packages = Array.isArray(data?.data) ? data.data : [];
        const billing = resolveUserBillingIds(local);

        if (
          shouldDowngradeToFreePlan(packages, {
            ...billing,
            free: local?.free,
          })
        ) {
          completeFreeDowngrade();
          return;
        }

        if (packages.length > 0) {
          if (hasMatchingActivePackage(packages, local.email)) {
            const paid = packages.find(
              pkg =>
                (pkg?.payment_status === 'active' || pkg?.payment_status === 'trialing') &&
                (!pkg?.email || normalizeEmail(pkg.email) === normalizeEmail(local.email))
            );
            if (paid?.subsId || paid?.planId) {
              try {
                const next = {
                  ...local,
                  free: false,
                  subId: paid.subsId || local.subId,
                  custId: paid.custId || local.custId,
                  planId: paid.planId || local.planId,
                };
                localStorage.setItem('userInfo', JSON.stringify(next));
              } catch {
                /* ignore storage errors */
              }
            }
            grantAccess();
            return;
          }

          // Packages exist but none active — free users still allowed (stale free flag recovery)
          if (isFreePlanUser(local)) {
            grantAccess();
            return;
          }

          bounceToShift();
          return;
        }

        // Explicit empty package list
        if (isFreePlanUser(local)) {
          grantAccess();
          return;
        }

        bounceToShift();
      })
      .catch(() => {
        if (cancelled) return;
        // Free users should not be kicked on package API failure
        if (isFreePlanUser(local)) {
          grantAccess();
          return;
        }
        // Paid session evidence: prefer staying in-app over a hard redirect loop
        if (local?.planId && local.planId !== 'free' && local.planId !== 'Free') {
          grantAccess();
          return;
        }
        bounceToShift();
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refreshToken, code, redirectTo, dispatch, navigate, fetchPackageData, location.pathname]);

  return (
    <>
      {children} {!showCurrent && local1 && isPlaying ? <AudioPlayer /> : ''}
    </>
  );
};

export default AuthenticatedRoute;
